import { ChartOfAccountsScreen } from "@sylviq/ui-react-web/screens";
import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from "@tanstack/react-router";
import { AccrualDetailPage } from "@/features/accruals/AccrualDetailPage";
import { AccrualsPage } from "@/features/accruals/AccrualsPage";
import { AgedPayablesPage } from "@/features/aging/AgedPayablesPage";
import { AgedReceivablesPage } from "@/features/aging/AgedReceivablesPage";
import { AuditLogPage } from "@/features/audit/AuditLogPage";
import { BankAccountDetailPage } from "@/features/banking/BankAccountDetailPage";
import { BankRulesPage } from "@/features/banking/BankRulesPage";
import { BankingPage } from "@/features/banking/BankingPage";
import { PaymentBatchesPage } from "@/features/banking/PaymentBatchesPage";
import { ScheduledPaymentsPage } from "@/features/banking/ScheduledPaymentsPage";
import { BillDetailPage } from "@/features/bills/BillDetailPage";
import { BillsPage } from "@/features/bills/BillsPage";
import { BudgetDetailPage } from "@/features/budgets/BudgetDetailPage";
import { BudgetsPage } from "@/features/budgets/BudgetsPage";
import { ChartAccountDetailPage } from "@/features/chart-of-accounts/ChartAccountDetailPage";
import { ChartAccountSettingsPage } from "@/features/chart-of-accounts/ChartAccountSettingsPage";
import { SourceJournalPosting } from "@/features/close/SourceJournalPosting";
import { ContactDetailPage } from "@/features/contacts/ContactDetailPage";
import { ContactsPage } from "@/features/contacts/ContactsPage";
import { CreditNoteDetailPage } from "@/features/credit-notes/CreditNoteDetailPage";
import { ExpenseApprovalsPage } from "@/features/expenses/ExpenseApprovalsPage";
import { ExtensionWorkspacePage } from "@/features/extensions/ExtensionWorkspacePage";
import { AssetsAndLoansPage } from "@/features/fixed-assets/AssetsAndLoansPage";
import { FixedAssetDetailPage } from "@/features/fixed-assets/FixedAssetDetailPage";
import { FixedAssetsPage } from "@/features/fixed-assets/FixedAssetsPage";
import { InvoiceDetailPage } from "@/features/invoices/InvoiceDetailPage";
import { InvoicesPage } from "@/features/invoices/InvoicesPage";
import { JournalEntryDetailPage } from "@/features/journal/JournalEntryDetailPage";
import { JournalPage } from "@/features/journal/JournalPage";
import { OpeningBalancesPage } from "@/features/opening-balances/OpeningBalancesPage";
import { LoanDetailPage } from "@/features/loans/LoanDetailPage";
import { LoansPage } from "@/features/loans/LoansPage";
import { OnboardingPage } from "@/features/onboarding/OnboardingPage";
import { OverviewPage } from "@/features/overview/OverviewPage";
import { PaymentsPage } from "@/features/payments/PaymentsPage";
import { PrepaymentDetailPage } from "@/features/prepayments/PrepaymentDetailPage";
import { PrepaymentsPage } from "@/features/prepayments/PrepaymentsPage";
import { QuoteDetailPage } from "@/features/quotes/QuoteDetailPage";
import { QuotesPage } from "@/features/quotes/QuotesPage";
import { ReceiptDetailPage } from "@/features/receipts/ReceiptDetailPage";
import { ReceiptsPage } from "@/features/receipts/ReceiptsPage";
import { RecurringInvoicesPage } from "@/features/recurring/RecurringInvoicesPage";
import { RecurringScheduleDetailPage } from "@/features/recurring/RecurringScheduleDetailPage";
import { RecurringBillScheduleDetailPage } from "@/features/recurring-bills/RecurringBillScheduleDetailPage";
import { RecurringBillsPage } from "@/features/recurring-bills/RecurringBillsPage";
import { RemindersPage } from "@/features/reminders/RemindersPage";
import { RepeatingEntriesPage } from "@/features/repeating-entries/RepeatingEntriesPage";
import { RepeatingEntryDetailPage } from "@/features/repeating-entries/RepeatingEntryDetailPage";
import { AnalyticReportPage } from "@/features/reports/AnalyticReportPage";
import { BalanceSheetPage } from "@/features/reports/BalanceSheetPage";
import { BudgetVsActualPage } from "@/features/reports/BudgetVsActualPage";
import { CashFlowPage } from "@/features/reports/CashFlowPage";
import { ContractorPaymentsPage } from "@/features/reports/ContractorPaymentsPage";
import { CultivationClosePage } from "@/features/reports/CultivationClosePage";
import { ExecutiveSummaryPage } from "@/features/reports/ExecutiveSummaryPage";
import { GlQueryPage } from "@/features/reports/GlQueryPage";
import { JobProfitabilityPage } from "@/features/reports/JobProfitabilityPage";
import { PnlComparisonPage } from "@/features/reports/PnlComparisonPage";
import { ProfitAndLossPage } from "@/features/reports/ProfitAndLossPage";
import { ReportsPage } from "@/features/reports/ReportsPage";
import { TaxEstimatesPage } from "@/features/tax/TaxEstimatesPage";
import { TrialBalancePage } from "@/features/reports/TrialBalancePage";
import { RevenueScheduleDetailPage } from "@/features/revenue/RevenueScheduleDetailPage";
import { RevenueSchedulesPage } from "@/features/revenue/RevenueSchedulesPage";
import { BooksSettingsPage } from "@/features/settings/BooksSettingsPage";
import { SettingsPage } from "@/features/settings/SettingsPage";
import { TaxSettingsPage } from "@/features/settings/TaxSettingsPage";
import { StockCostDetailPage } from "@/features/stock-costs/StockCostDetailPage";
import { StockCostsPage } from "@/features/stock-costs/StockCostsPage";
import { SupplierCreditDetailPage } from "@/features/supplier-credits/SupplierCreditDetailPage";
import { SupplierCreditsPage } from "@/features/supplier-credits/SupplierCreditsPage";
import { TaxPage } from "@/features/tax/TaxPage";
import { RequireAccountingSetup, RequireAuth, RequireOrganization } from "@/lib/route-guards";
import { useViewSearch } from "@/lib/view-search";
import { SuiNavigationHost } from "@/lib/sui-operations";
import { AppShell } from "@/shell/AppShell";

const rootRoute = createRootRoute({ component: Outlet });

const onboardingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/onboarding",
  component: () => (
    <RequireAuth>
      <RequireOrganization>
        <OnboardingPage />
      </RequireOrganization>
    </RequireAuth>
  ),
});

// Pathless layout route: every organization page renders inside the app shell,
// signed-in, with a Console-provided (organization, legal entity)
// scope. Login and accounting setup stay outside the nav chrome.
const shellRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_shell",
  component: () => (
    <RequireAuth>
      <RequireOrganization>
        <RequireAccountingSetup>
          <SuiNavigationHost>
            <AppShell />
          </SuiNavigationHost>
        </RequireAccountingSetup>
      </RequireOrganization>
    </RequireAuth>
  ),
});

const overviewRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/",
  component: OverviewPage,
});

const extensionsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/settings/imports",
  beforeLoad: () => {
    throw redirect({
      to: "/extensions/$publisher/$key/$page",
      params: { publisher: "sylviq", key: "niche-cultivation-costing", page: "imports" },
      search: true,
      replace: true,
    });
  },
});

const legacyImportReportsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/reports/imports",
  beforeLoad: () => {
    throw redirect({
      to: "/extensions/$publisher/$key/$page",
      params: { publisher: "sylviq", key: "niche-cultivation-costing", page: "imports" },
      search: true,
      replace: true,
    });
  },
});

const metrcRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/metrc",
  beforeLoad: () => {
    throw redirect({
      to: "/extensions/$publisher/$key/$page",
      params: { publisher: "sylviq", key: "niche-cultivation-costing", page: "imports" },
      search: true,
      replace: true,
    });
  },
});

const extensionWorkspaceRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/extensions/$publisher/$key/$page",
  component: ExtensionWorkspacePage,
});

const chartOfAccountsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/chart-of-accounts",
  component: ChartOfAccountsScreen,
});

const chartAccountDetailRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/chart-of-accounts/$accountId",
  component: ChartAccountDetailPage,
});

const chartAccountSettingsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/chart-of-accounts/$accountId/settings",
  component: ChartAccountSettingsPage,
});

const journalRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/journal",
  component: JournalPage,
});

const openingBalancesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/opening-balances",
  component: OpeningBalancesPage,
});

const journalEntryRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/journal/$entryId",
  component: JournalEntryDetailPage,
});

const invoicesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/invoices",
  component: InvoicesPage,
});

const invoiceDetailRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/invoices/$invoiceId",
  component: InvoiceDetailPage,
});

const quotesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/quotes",
  component: QuotesPage,
});

const quoteNewRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/quotes/new",
  component: QuoteDetailPage,
});

const quoteDetailRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/quotes/$quoteId",
  component: QuoteDetailPage,
});

const billsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/bills",
  component: BillsPage,
});

const billDetailRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/bills/$billId",
  component: BillDetailPage,
});

const paymentsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/payments",
  component: PaymentsPage,
});

const creditNoteDetailRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/credit-notes/$creditNoteId",
  component: CreditNoteDetailPage,
});

const recurringInvoicesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/recurring-invoices",
  component: RecurringInvoicesPage,
});

const recurringScheduleDetailRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/recurring-invoices/$recurringScheduleId",
  component: RecurringScheduleDetailPage,
});

const prepaymentsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/prepayments",
  component: PrepaymentsPage,
});
const prepaymentDetailRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/prepayments/$prepaymentId",
  component: PrepaymentDetailPage,
});

const accrualsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/accruals",
  component: AccrualsPage,
});

const accrualDetailRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/accruals/$accrualScheduleId",
  component: AccrualDetailPage,
});

const repeatingEntriesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/recurring-journals",
  beforeLoad: () => {
    throw redirect({
      to: "/journal",
      search: (previous: Record<string, unknown>) => ({
        ...previous,
        view: "repeating",
      }),
      replace: true,
    });
  },
  component: RepeatingEntriesPage,
});

const repeatingEntryDetailRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/recurring-journals/$templateId",
  component: RepeatingEntryDetailPage,
});

const budgetsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/settings/budgets",
  beforeLoad: () => {
    throw redirect({
      to: "/settings",
      search: (previous: Record<string, unknown>) => ({
        ...previous,
        tab: "planning",
      }),
      replace: true,
    });
  },
  component: BudgetsPage,
});

const legacyBudgetsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/budgets",
  beforeLoad: () => {
    throw redirect({
      to: "/settings/budgets",
      search: (previous: Record<string, unknown>) => previous,
      replace: true,
    });
  },
  component: BudgetsPage,
});

const budgetDetailRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/settings/budgets/$budgetId",
  component: BudgetDetailPage,
});

const legacyBudgetDetailRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/budgets/$budgetId",
  beforeLoad: ({ params }) => {
    throw redirect({
      to: "/settings/budgets/$budgetId",
      params,
      search: (previous: Record<string, unknown>) => previous,
      replace: true,
    });
  },
  component: BudgetDetailPage,
});

const loansRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/loans",
  beforeLoad: () => {
    throw redirect({
      to: "/assets-and-loans",
      search: (previous: Record<string, unknown>) => ({
        ...previous,
        tab: "loans",
      }),
      replace: true,
    });
  },
  component: LoansPage,
});

const loanDetailRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/loans/$loanId",
  component: LoanDetailPage,
});

const fixedAssetsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/fixed-assets",
  beforeLoad: () => {
    throw redirect({
      to: "/assets-and-loans",
      search: (previous: Record<string, unknown>) => ({
        ...previous,
        tab: "fixed-assets",
      }),
      replace: true,
    });
  },
  component: FixedAssetsPage,
});

const assetsAndLoansRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/assets-and-loans",
  component: AssetsAndLoansPage,
});

const fixedAssetDetailRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/fixed-assets/$assetId",
  component: FixedAssetDetailPage,
});

const supplierCreditsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/supplier-credits",
  component: SupplierCreditsPage,
});

const supplierCreditDetailRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/supplier-credits/$supplierCreditId",
  component: SupplierCreditDetailPage,
});

const recurringBillsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/recurring-bills",
  component: RecurringBillsPage,
});

const recurringBillScheduleDetailRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/recurring-bills/$recurringBillScheduleId",
  component: RecurringBillScheduleDetailPage,
});

const expensesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/expenses",
  component: ExpenseApprovalsPage,
});

const stockCostsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/stock-costs",
  component: StockCostsPage,
});

const stockCostDetailRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/stock-costs/$stockCostId",
  component: StockCostDetailPage,
});

const receiptsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/receipts",
  component: ReceiptsPage,
});

const receiptDetailRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/receipts/$paymentId",
  component: ReceiptDetailPage,
});

const revenueSchedulesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/revenue-schedules",
  component: RevenueSchedulesPage,
});

const revenueScheduleDetailRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/revenue-schedules/$revenueScheduleId",
  component: RevenueScheduleDetailPage,
});

const agedReceivablesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/aged-receivables",
  component: AgedReceivablesPage,
});

const agedPayablesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/aged-payables",
  component: AgedPayablesPage,
});

const sourceJournalsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/source-journals",
  component: SourceJournalPosting,
});

const booksSettingsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/settings/books",
  beforeLoad: () => {
    throw redirect({
      to: "/settings",
      search: (previous: Record<string, unknown>) => ({
        ...previous,
        tab: "books",
      }),
      replace: true,
    });
  },
  component: BooksSettingsPage,
});

// Phase 7: one contacts list only. `/contacts` is the unified bookkeeping
// list (balance + overdue, CRM deep-links per row); `/contacts/$contactId`
// is the Bookkeeping + Statement detail. The old `/contacts-list` link-list
// route and the lookup-by-id profile page are gone.
const contactsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/contacts",
  component: ContactsPage,
});

const contactDetailRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/contacts/$contactId",
  component: ContactDetailPage,
});

const trialBalanceRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/trial-balance",
  component: TrialBalancePage,
});

const balanceSheetRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/balance-sheet",
  component: BalanceSheetPage,
});

const profitAndLossRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/profit-and-loss",
  component: ProfitAndLossPage,
});

const cashFlowRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/cash-flow",
  component: CashFlowPage,
});

const glQueryRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/gl-query",
  component: GlQueryPage,
});

function RemindersRoute() {
  const [section] = useViewSearch("section", ["rules"], "queue");
  return section === "rules" ? <RemindersPage rulesOnly /> : <RemindersPage />;
}

const remindersRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/reminders",
  component: RemindersRoute,
});

const auditLogRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/audit-log",
  component: AuditLogPage,
});

// Phase 7 move-out: sell-side SaaS subscription billing is not bookkeeping.
// The BillingPage route and its lib/api.ts functions are deleted — that
// surface belongs to platform-admin, never to a /billing route here.

const bankingRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/banking",
  component: BankingPage,
});

const bankRulesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/banking/rules",
  component: BankRulesPage,
});

const bankAccountDetailRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/banking/$accountId",
  component: BankAccountDetailPage,
});

const paymentBatchesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/banking/batches",
  component: PaymentBatchesPage,
});

const scheduledPaymentsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/banking/scheduled",
  component: ScheduledPaymentsPage,
});

const taxRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/tax",
  beforeLoad: ({ search }) => {
    const section = (search as Record<string, unknown>).section;
    const settingsSection = ["rates", "brackets", "allowances", "lookup"].includes(
      String(section),
    );
    if (settingsSection) {
      throw redirect({
        to: "/settings/tax",
        search: (previous: Record<string, unknown>) => ({
          ...previous,
          section,
        }),
        replace: true,
      });
    }
    if (section === "contractors") {
      throw redirect({
        to: "/reports/contractor-payments",
        search: (previous: Record<string, unknown>) => previous,
        replace: true,
      });
    }
    if (section === "estimates") {
      throw redirect({
        to: "/reports/tax-estimates",
        search: (previous: Record<string, unknown>) => previous,
        replace: true,
      });
    }
  },
  component: TaxPage,
});

const taxSettingsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/settings/tax",
  beforeLoad: () => {
    throw redirect({
      to: "/settings",
      search: (previous: Record<string, unknown>) => ({
        ...previous,
        tab: "tax",
      }),
      replace: true,
    });
  },
  component: TaxSettingsPage,
});

const currenciesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/currencies",
  beforeLoad: () => {
    throw redirect({
      to: "/settings",
      search: (previous: Record<string, unknown>) => ({
        ...previous,
        tab: "currencies",
      }),
      replace: true,
    });
  },
  component: SettingsPage,
});

const reportsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/reports",
  component: ReportsPage,
});

const contractorPaymentsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/reports/contractor-payments",
  component: ContractorPaymentsPage,
});

const cultivationCloseRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/reports/cultivation-close",
  component: CultivationClosePage,
});

const taxEstimatesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/tax/estimates",
  component: TaxEstimatesPage,
});

const legacyTaxEstimatesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/reports/tax-estimates",
  beforeLoad: () => {
    throw redirect({
      to: "/tax/estimates",
      search: (previous: Record<string, unknown>) => previous,
      replace: true,
    });
  },
  component: TaxEstimatesPage,
});

const analyticReportRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/reports/analytic",
  component: AnalyticReportPage,
});

const jobProfitabilityRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/reports/job-profitability",
  component: JobProfitabilityPage,
});

const budgetVsActualRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/reports/budget-vs-actual",
  component: BudgetVsActualPage,
});

const pnlComparisonRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/reports/pnl-comparison",
  component: PnlComparisonPage,
});

const executiveSummaryRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/reports/executive-summary",
  component: ExecutiveSummaryPage,
});

const settingsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/settings",
  component: SettingsPage,
});

const accountingSettingsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/settings/accounting",
  beforeLoad: () => {
    throw redirect({
      to: "/settings",
      search: (previous: Record<string, unknown>) => ({
        ...previous,
        tab: "currencies",
      }),
      replace: true,
    });
  },
  component: SettingsPage,
});

const routeTree = rootRoute.addChildren([
  onboardingRoute,
  shellRoute.addChildren([
    overviewRoute,
    extensionsRoute,
    legacyImportReportsRoute,
    metrcRoute,
    extensionWorkspaceRoute,
    chartOfAccountsRoute,
    chartAccountDetailRoute,
    chartAccountSettingsRoute,
    journalRoute,
    openingBalancesRoute,
    journalEntryRoute,
    invoicesRoute,
    invoiceDetailRoute,
    quotesRoute,
    quoteNewRoute,
    quoteDetailRoute,
    billsRoute,
    billDetailRoute,
    paymentsRoute,
    creditNoteDetailRoute,
    recurringInvoicesRoute,
    recurringScheduleDetailRoute,
    prepaymentsRoute,
    prepaymentDetailRoute,
    accrualsRoute,
    accrualDetailRoute,
    repeatingEntriesRoute,
    repeatingEntryDetailRoute,
    budgetsRoute,
    legacyBudgetsRoute,
    budgetDetailRoute,
    legacyBudgetDetailRoute,
    loansRoute,
    loanDetailRoute,
    fixedAssetsRoute,
    fixedAssetDetailRoute,
    assetsAndLoansRoute,
    supplierCreditsRoute,
    supplierCreditDetailRoute,
    recurringBillsRoute,
    recurringBillScheduleDetailRoute,
    expensesRoute,
    stockCostsRoute,
    stockCostDetailRoute,
    receiptsRoute,
    receiptDetailRoute,
    revenueSchedulesRoute,
    revenueScheduleDetailRoute,
    agedReceivablesRoute,
    agedPayablesRoute,
    sourceJournalsRoute,
    booksSettingsRoute,
    contactsRoute,
    contactDetailRoute,
    trialBalanceRoute,
    balanceSheetRoute,
    profitAndLossRoute,
    cashFlowRoute,
    glQueryRoute,
    remindersRoute,
    auditLogRoute,
    bankingRoute,
    bankRulesRoute,
    bankAccountDetailRoute,
    paymentBatchesRoute,
    scheduledPaymentsRoute,
    taxRoute,
    currenciesRoute,
    reportsRoute,
    contractorPaymentsRoute,
    cultivationCloseRoute,
    taxEstimatesRoute,
    legacyTaxEstimatesRoute,
    analyticReportRoute,
    jobProfitabilityRoute,
    budgetVsActualRoute,
    pnlComparisonRoute,
    executiveSummaryRoute,
    settingsRoute,
    taxSettingsRoute,
    accountingSettingsRoute,
  ]),
]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
